import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Layout/Header';
import {
  activityLogsService,
  ActivityLogEntry,
} from '../services/activity-logs.service';
import { formatDateTime } from '../utils/helpers';
import './AdminActivityLog.css';

const RESOURCE_LABEL: Record<string, string> = {
  POST: 'Post',
  DOCUMENT: 'Document',
  FOLDER: 'Folder',
  POLL: 'Poll',
  POLICY: 'Policy',
  MESSAGE: 'Sidebar message',
  INFO_ITEM: 'Info / quick link card',
};

const ACTION_LABEL: Record<string, string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  END: 'Ended',
};

const AdminActivityLog: React.FC = () => {
  const [rows, setRows] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterResource, setFilterResource] = useState<string>('');
  const [filterAction, setFilterAction] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await activityLogsService.getAll();
        if (!cancelled) setRows(data);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.message || e?.message || 'Failed to load activity log');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resourceOptions = useMemo(() => {
    const set = new Set(rows.map((r) => r.resource));
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filterResource && r.resource !== filterResource) return false;
      if (filterAction && r.action !== filterAction) return false;
      return true;
    });
  }, [rows, filterResource, filterAction]);

  return (
    <div className="admin-activity-page">
      <Header />
      <main className="admin-activity-main">
        <div className="admin-activity-header">
          <div>
            <h1>Activity log</h1>
            <p className="admin-activity-sub">
              Auditable changes to posts, documents, folders, polls, policies, sidebar messages, and
              info cards. Admin only.
            </p>
          </div>
          <Link to="/" className="admin-activity-back">
            ← Back to home
          </Link>
        </div>

        {error && <div className="admin-activity-error">{error}</div>}

        <div className="admin-activity-toolbar">
          <label>
            <span>Resource</span>
            <select
              value={filterResource}
              onChange={(e) => setFilterResource(e.target.value)}
              disabled={loading}
            >
              <option value="">All</option>
              {resourceOptions.map((r) => (
                <option key={r} value={r}>
                  {RESOURCE_LABEL[r] || r}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Action</span>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              disabled={loading}
            >
              <option value="">All</option>
              <option value="CREATE">Created</option>
              <option value="UPDATE">Updated</option>
              <option value="DELETE">Deleted</option>
              <option value="END">Ended</option>
            </select>
          </label>
          <span className="admin-activity-count">
            {loading ? 'Loading…' : `${filtered.length} event${filtered.length === 1 ? '' : 's'}`}
          </span>
        </div>

        <div className="admin-activity-table-wrap">
          <table className="admin-activity-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Action</th>
                <th>Resource</th>
                <th>ID</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="admin-activity-empty">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="admin-activity-empty">
                    No events match your filters.
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((r) => (
                  <tr key={r.id}>
                    <td className="admin-activity-when">{formatDateTime(r.createdAt)}</td>
                    <td className="admin-activity-who">
                      {r.user?.fullName || '—'}
                      {r.user?.email && (
                        <span className="admin-activity-email">{r.user.email}</span>
                      )}
                    </td>
                    <td>
                      <span className={`act-badge act-${r.action.toLowerCase()}`}>
                        {ACTION_LABEL[r.action] || r.action}
                      </span>
                    </td>
                    <td>{RESOURCE_LABEL[r.resource] || r.resource}</td>
                    <td className="admin-activity-id">{r.resourceId || '—'}</td>
                    <td className="admin-activity-summary">{r.summary || '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default AdminActivityLog;
